import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Box, CircularProgress, Typography } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import debounce from 'lodash/debounce';
import { useEventsApi, useReceiptsApi } from 'services/api';
import { CHECK_IN_COLORS } from './colors';
import { formatCheckedInTimeOnly, formatOpensAtLabel } from './format';
import { toResultKind } from './types';

interface EmailRosterTabProps {
  eventId: string;
}

interface LookupRow {
  userId: string;
  firstName: string;
  lastName: string;
  email: string;
  onRsvpList: boolean;
  isOutsideGuest: boolean;
  isSelf: boolean;
  checkedInAt?: string;
  ticketType?: string;
}

type RowState = 'checked_in' | 'self' | 'outside_guest' | 'walk_in' | 'rsvp_required' | 'ready';

const rowState = (row: LookupRow, allowWalkIn: boolean): RowState => {
  if (row.checkedInAt) return 'checked_in';
  if (row.isSelf) return 'self';
  if (row.isOutsideGuest && row.onRsvpList) return 'outside_guest';
  if (!row.onRsvpList && allowWalkIn && !row.isOutsideGuest) return 'walk_in';
  if (!row.onRsvpList) return 'rsvp_required';
  return 'ready';
};

const Tag: React.FC<{ label: string; color: string; background: string }> = ({
  label,
  color,
  background,
}) => (
  <Box
    sx={{
      alignSelf: 'flex-start',
      backgroundColor: background,
      borderRadius: '9999px',
      px: '8px',
      py: '2px',
    }}
  >
    <Typography
      sx={{
        fontFamily: 'Poppins, sans-serif',
        fontWeight: 600,
        fontSize: '11px',
        lineHeight: '16px',
        letterSpacing: '0.04em',
        color,
      }}
    >
      {label}
    </Typography>
  </Box>
);

export const EmailRosterTab: React.FC<EmailRosterTabProps> = ({ eventId }) => {
  const eventsApi = useEventsApi();
  const receiptsApi = useReceiptsApi();
  const [query, setQuery] = useState('');
  const [rows, setRows] = useState<LookupRow[]>([]);
  const [allowWalkIn, setAllowWalkIn] = useState(false);
  const [loading, setLoading] = useState(false);
  const [checkingUserId, setCheckingUserId] = useState<string | null>(null);
  const [guestConfirm, setGuestConfirm] = useState<LookupRow | null>(null);
  const [notice, setNotice] = useState('');
  const searchGenRef = useRef(0);
  const queryRef = useRef('');

  const fetchRows = useCallback(
    async (value: string) => {
      const trimmed = value.trim();
      if (!trimmed) {
        searchGenRef.current += 1;
        setRows([]);
        setLoading(false);
        setNotice('');
        return;
      }

      const gen = ++searchGenRef.current;
      setLoading(true);
      const result = await eventsApi.lookupCheckIn(eventId, trimmed);
      if (gen !== searchGenRef.current) return;

      if (result.success) {
        setRows(result.value.rows);
        setAllowWalkIn(!!result.value.allowWalkInCheckIn);
      } else {
        setRows([]);
      }
      setLoading(false);
    },
    [eventId, eventsApi],
  );

  const fetchRowsRef = useRef(fetchRows);
  fetchRowsRef.current = fetchRows;

  const searchDebounced = useMemo(
    () =>
      debounce((value: string) => {
        void fetchRowsRef.current(value);
      }, 200),
    [],
  );

  useEffect(() => () => searchDebounced.cancel(), [searchDebounced]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setQuery(value);
    queryRef.current = value;
    setNotice('');
    setGuestConfirm(null);
    searchDebounced(value);
  };

  const refresh = async () => {
    const gen = ++searchGenRef.current;
    const result = await eventsApi.lookupCheckIn(
      eventId,
      queryRef.current.trim(),
    );
    if (gen === searchGenRef.current && result.success) {
      setRows(result.value.rows);
      setAllowWalkIn(!!result.value.allowWalkInCheckIn);
    }
  };

  const handleCheckIn = async (row: LookupRow, confirmGuest = false) => {
    const state = rowState(row, allowWalkIn);
    if (state === 'self' || state === 'rsvp_required' || state === 'checked_in') {
      return;
    }
    if (state === 'outside_guest' && !confirmGuest) {
      setGuestConfirm(row);
      return;
    }

    setCheckingUserId(row.userId);
    searchDebounced.cancel();
    searchGenRef.current += 1;
    setNotice('');

    const response = await receiptsApi.checkInAttendee(
      eventId,
      row.userId,
      confirmGuest,
    );

    if (!response.success) {
      setNotice('Could not check in this student.');
      setCheckingUserId(null);
      return;
    }

    const kind = toResultKind(response.value.status);
    if (kind === 'outside_guest') {
      setGuestConfirm(row);
      setCheckingUserId(null);
      return;
    }
    if (kind === 'checked_in' || kind === 'already_checked_in') {
      setGuestConfirm(null);
      await refresh();
      setCheckingUserId(null);
      return;
    }

    if (kind === 'not_open') {
      const opens = formatOpensAtLabel(response.value.checkInOpensAt);
      setNotice(
        opens ? `Check-in opens at ${opens}.` : 'Check-in is not open yet.',
      );
    } else if (kind === 'closed') {
      setNotice('Check-in for this event has ended.');
    } else if (kind === 'unverified') {
      setNotice('Only verified accounts can be checked in.');
    } else if (kind === 'self_blocked') {
      setNotice("You can't check yourself in while checking in attendees.");
    } else if (kind === 'rsvp_required') {
      setNotice('This student is not on the RSVP list.');
    } else {
      setNotice('Could not check in this student.');
    }
    setGuestConfirm(null);
    setCheckingUserId(null);
  };

  return (
    <Box
      sx={{
        flex: 1,
        minHeight: 0,
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          backgroundColor: CHECK_IN_COLORS.sheetBg,
          display: 'flex',
          flexDirection: 'column',
          gap: '14px',
          pt: '24px',
          px: '20px',
          pb: '20px',
          overflow: 'auto',
        }}
      >
        <Box
          sx={{
            backgroundColor: CHECK_IN_COLORS.white,
            border: `0.5px solid ${CHECK_IN_COLORS.searchBorder}`,
            borderRadius: '8px',
            height: '48px',
            display: 'flex',
            alignItems: 'center',
            gap: '10px',
            px: '14px',
            width: '100%',
            flexShrink: 0,
          }}
        >
          <SearchIcon sx={{ fontSize: 20, color: CHECK_IN_COLORS.text }} />
          <Box
            component="input"
            value={query}
            onChange={handleChange}
            placeholder="Search name or email"
            sx={{
              border: 'none',
              outline: 'none',
              flex: 1,
              fontFamily: 'Poppins, sans-serif',
              fontSize: '14.5px',
              lineHeight: '22px',
              color: CHECK_IN_COLORS.text,
              background: 'transparent',
              '&::placeholder': { color: CHECK_IN_COLORS.muted },
            }}
          />
          {loading && (
            <CircularProgress size={18} sx={{ color: CHECK_IN_COLORS.navy }} />
          )}
        </Box>

        {notice ? (
          <Typography
            sx={{
              fontFamily: 'Poppins, sans-serif',
              fontWeight: 400,
              fontSize: '13px',
              lineHeight: '20px',
              color: CHECK_IN_COLORS.red,
            }}
          >
            {notice}
          </Typography>
        ) : null}

        {query.trim() && !loading && rows.length === 0 ? (
          <Typography
            sx={{
              fontFamily: 'Poppins, sans-serif',
              fontWeight: 400,
              fontSize: '14px',
              lineHeight: '22px',
              color: CHECK_IN_COLORS.textSecondary,
            }}
          >
            No student with this email
          </Typography>
        ) : null}

        {rows.length > 0 && (
          <Box
            sx={{
              backgroundColor: CHECK_IN_COLORS.white,
              border: `1px solid ${CHECK_IN_COLORS.rosterBorder}`,
              boxShadow: CHECK_IN_COLORS.rosterShadow,
              width: '100%',
              overflow: 'hidden',
            }}
          >
            {rows.map((row, index) => {
              const name = [row.firstName, row.lastName].filter(Boolean).join(' ');
              const state = rowState(row, allowWalkIn);
              const disabled = state === 'self';

              return (
                <Box
                  key={`${row.userId}-${row.email || index}`}
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '12px',
                    px: '16px',
                    py: '14px',
                    borderTop:
                      index === 0
                        ? 'none'
                        : `1px solid ${CHECK_IN_COLORS.rosterDivider}`,
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '6px',
                      color: CHECK_IN_COLORS.text,
                    }}
                  >
                    <Typography
                      sx={{
                        fontFamily: 'Poppins, sans-serif',
                        fontWeight: 600,
                        fontSize: '16px',
                        lineHeight: '22px',
                      }}
                    >
                      {name}
                    </Typography>
                    <Typography
                      sx={{
                        fontFamily: 'Poppins, sans-serif',
                        fontWeight: 400,
                        fontSize: '12px',
                        lineHeight: '18px',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {row.email}
                    </Typography>
                    {state === 'walk_in' ? (
                      <Tag
                        label="WALK-IN · NOT ON RSVP LIST"
                        color={CHECK_IN_COLORS.navy}
                        background={CHECK_IN_COLORS.surfaceSubtle}
                      />
                    ) : null}
                    {state === 'rsvp_required' ? (
                      <Tag
                        label="NOT ON RSVP LIST"
                        color={CHECK_IN_COLORS.red}
                        background={CHECK_IN_COLORS.redSoft}
                      />
                    ) : null}
                    {state === 'outside_guest' ? (
                      <Tag
                        label="OUTSIDE GUEST"
                        color={CHECK_IN_COLORS.amber}
                        background={CHECK_IN_COLORS.amberSoft}
                      />
                    ) : null}
                  </Box>

                  {state === 'checked_in' ? (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                      <CheckCircleIcon
                        sx={{ fontSize: 16, color: CHECK_IN_COLORS.green }}
                      />
                      <Typography
                        sx={{
                          fontFamily: 'Poppins, sans-serif',
                          fontWeight: 500,
                          fontSize: '12px',
                          lineHeight: '18px',
                          color: CHECK_IN_COLORS.green,
                        }}
                      >
                        Checked in · {formatCheckedInTimeOnly(row.checkedInAt)}
                      </Typography>
                    </Box>
                  ) : state === 'rsvp_required' ? (
                    <Typography
                      sx={{
                        fontFamily: 'Poppins, sans-serif',
                        fontWeight: 400,
                        fontSize: '12px',
                        lineHeight: '18px',
                        color: CHECK_IN_COLORS.textSecondary,
                      }}
                    >
                      Change Check-in settings to allow walk-ins.
                    </Typography>
                  ) : (
                    <>
                      <Box
                        onClick={() => {
                          if (disabled || checkingUserId === row.userId) return;
                          void handleCheckIn(row);
                        }}
                        sx={{
                          backgroundColor: disabled
                            ? CHECK_IN_COLORS.hairline
                            : CHECK_IN_COLORS.gold,
                          height: '45px',
                          borderRadius: '8px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          cursor: disabled ? 'default' : 'pointer',
                          opacity: checkingUserId === row.userId ? 0.7 : 1,
                          width: '100%',
                        }}
                      >
                        {checkingUserId === row.userId ? (
                          <CircularProgress
                            size={18}
                            sx={{ color: CHECK_IN_COLORS.goldText }}
                          />
                        ) : (
                          <Typography
                            sx={{
                              fontFamily: 'Poppins, sans-serif',
                              fontWeight: 500,
                              fontSize: '14px',
                              color: disabled
                                ? CHECK_IN_COLORS.muted
                                : CHECK_IN_COLORS.goldText,
                            }}
                          >
                            Check in
                          </Typography>
                        )}
                      </Box>
                      {state === 'self' ? (
                        <Typography
                          sx={{
                            fontFamily: 'Poppins, sans-serif',
                            fontWeight: 400,
                            fontSize: '12px',
                            lineHeight: '18px',
                            color: CHECK_IN_COLORS.textSecondary,
                          }}
                        >
                          You can't check yourself in while checking in
                          attendees.
                        </Typography>
                      ) : null}
                    </>
                  )}
                </Box>
              );
            })}
          </Box>
        )}
      </Box>

      {guestConfirm ? (
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            backgroundColor: 'rgba(20, 31, 51, 0.45)',
            display: 'flex',
            alignItems: 'flex-end',
            zIndex: 2,
          }}
        >
          <Box
            sx={{
              width: '100%',
              backgroundColor: CHECK_IN_COLORS.white,
              borderRadius: '16px 16px 0 0',
              px: '20px',
              pt: '20px',
              pb: '24px',
              display: 'flex',
              flexDirection: 'column',
              gap: '12px',
            }}
          >
            <Typography
              sx={{
                fontFamily: 'Poppins, sans-serif',
                fontWeight: 600,
                fontSize: '18px',
                lineHeight: '26px',
                color: CHECK_IN_COLORS.text,
              }}
            >
              Check in outside guest?
            </Typography>
            <Typography
              sx={{
                fontFamily: 'Poppins, sans-serif',
                fontWeight: 400,
                fontSize: '14px',
                lineHeight: '21px',
                color: CHECK_IN_COLORS.textSecondary,
              }}
            >
              Not a school email. Confirm to check them in.
            </Typography>
            <Box
              onClick={() => {
                void handleCheckIn(guestConfirm, true);
              }}
              sx={{
                mt: '4px',
                height: '48px',
                borderRadius: '8px',
                backgroundColor: CHECK_IN_COLORS.gold,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
              }}
            >
              <Typography
                sx={{
                  fontFamily: 'Poppins, sans-serif',
                  fontWeight: 500,
                  fontSize: '14px',
                  color: CHECK_IN_COLORS.goldText,
                }}
              >
                Check in guest
              </Typography>
            </Box>
            <Box
              onClick={() => setGuestConfirm(null)}
              sx={{
                height: '48px',
                borderRadius: '8px',
                border: `1px solid ${CHECK_IN_COLORS.border}`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
              }}
            >
              <Typography
                sx={{
                  fontFamily: 'Poppins, sans-serif',
                  fontWeight: 500,
                  fontSize: '14px',
                  color: CHECK_IN_COLORS.navy,
                }}
              >
                Cancel
              </Typography>
            </Box>
          </Box>
        </Box>
      ) : null}
    </Box>
  );
};
